import { useTranslation } from 'react-i18next'
import { Pressable, StyleSheet, View, Image } from 'react-native'
import Markdown from 'markdown-to-jsx'
import { Color, Icon, Radius, Spacing, Text } from 'zens-storybook'

import { Tooltip } from '../tooltip'
import PreviewTable from './preview-table'
import {
  formatTimestamp,
  markdownWrapperStyle,
  defaultMarkdownOptions,
  errorMarkdownOptions,
  normalizeMarkdown,
  flattenMarkdownLinks,
  markdownToPlainText,
} from './constants'
import type { ChatMessage, ReportPreview } from './types'
import { useTypewriter } from './use-typewriter'

const AnimatedDots = () => (
  <Image 
    source={require('../../../assets/loading-dots.webp')} 
    style={{ width: 24, height: 8, marginLeft: Spacing.XS }} 
  />
)

interface ChatMessageBubbleProps {
  message: ChatMessage
  hoveredPreviewAction: { messageId: string; action: 'preview' | 'download' } | null
  setHoveredPreviewAction: (value: { messageId: string; action: 'preview' | 'download' } | null | ((current: { messageId: string; action: 'preview' | 'download' } | null) => { messageId: string; action: 'preview' | 'download' } | null)) => void
  hoveredMessageAction: { messageId: string; action: 'copy' } | null
  setHoveredMessageAction: (value: { messageId: string; action: 'copy' } | null | ((current: { messageId: string; action: 'copy' } | null) => { messageId: string; action: 'copy' } | null)) => void
  copiedMessageId: string | null
  onCopyMessage: (messageId: string, content: string) => void
  onExpandPreview: (preview: ReportPreview) => void
  onDownloadReport: (url?: string) => void
}

const ChatMessageBubble = ({
  message,
  hoveredPreviewAction,
  setHoveredPreviewAction,
  hoveredMessageAction,
  setHoveredMessageAction,
  copiedMessageId,
  onCopyMessage,
  onExpandPreview,
  onDownloadReport,
}: ChatMessageBubbleProps) => {
  const { t } = useTranslation()
  const isUser = message.role === 'user'
  const hasStatusMessage = !isUser && message.statusMessage && message.status !== 'done' && message.status !== 'error'
  const shouldRenderBubble =
    isUser ||
    !!message.content ||
    !!message.reportPreview ||
    !!message.reportPreviewLoading ||
    !!message.reportPreviewError ||
    message.status === 'error'
  const showDownloadAction =
    !!message.reportPreview || message.reportStatus === 'pending' || message.reportStatus === 'ready'
  const canDownloadReport = message.reportStatus === 'ready' && !!message.reportUrl

  const wrapperStyles = [styles.messageWrapper, isUser ? styles.userWrapper : styles.assistantWrapper]
  const timestampStyles = [styles.timestampText, isUser ? styles.userTimestamp : styles.assistantTimestamp]
  const isCopyTooltipVisible =
    hoveredMessageAction?.messageId === message.id && hoveredMessageAction?.action === 'copy'
  const isErrorMessage = !isUser && message.status === 'error'
  const fullContent = message.content || ''
  const { text: displayedContent, isDone: typewriterDone } = useTypewriter(flattenMarkdownLinks(fullContent), {
    skip: isUser || message.status === 'done' || message.status === 'error',
  })
  const markdownContent = typewriterDone
    ? normalizeMarkdown(fullContent)
    : normalizeMarkdown(displayedContent)
  const copyContent = isUser ? fullContent : markdownToPlainText(fullContent)
  const bubbleStyles = [
    styles.messageBubble,
    isUser ? styles.userBubble : styles.assistantBubble,
    isErrorMessage && styles.errorBubble,
  ]
  const textStyles = [isUser ? styles.userText : styles.assistantText, isErrorMessage && styles.errorText]
  const downloadIcon = (
    <Icon
      name="download"
      size={18}
      color={canDownloadReport ? Color.premiumBlack : Color.graphicGrey}
    />
  )

  return (
    <View style={[styles.messageRow, isUser ? styles.userMessage : styles.assistantMessage]}>
      <View style={wrapperStyles}>
        {shouldRenderBubble && (
          <View style={bubbleStyles}>
            {isUser ? (
              <Text style={textStyles}>{message.content}</Text>
            ) : (
              <div style={markdownWrapperStyle(
                isErrorMessage ? Color.darkRed : Color.premiumBlack,
                isErrorMessage ? Color.darkRed : Color.darkerBlue,
              )}>
                <Markdown options={isErrorMessage ? errorMarkdownOptions : defaultMarkdownOptions}>
                  {markdownContent}
                </Markdown>
              </div>
            )}

            {message.reportPreviewLoading && (
              <Text style={styles.statusText}>{t('Loading preview...')}</Text>
            )}

            {message.reportPreviewError && (
              <Text style={styles.previewError}>{message.reportPreviewError}</Text>
            )}

            {message.reportPreview && (
              <View style={styles.previewContainer}>
                <PreviewTable data={message.reportPreview} variant="inline" />
              </View>
            )}

            {(message.reportPreview || showDownloadAction) && (
              <View style={styles.previewFooter}>
                {message.reportPreview && (
                  <Tooltip
                    label={t('Preview')}
                    visible={
                      hoveredPreviewAction?.messageId === message.id &&
                      hoveredPreviewAction?.action === 'preview'
                    }
                    style={styles.previewActionWrapper}
                  >
                    <View
                      onPointerEnter={() => setHoveredPreviewAction({ messageId: message.id, action: 'preview' })}
                      onPointerLeave={() => setHoveredPreviewAction((current) => (current?.messageId === message.id && current?.action === 'preview' ? null : current))}
                    >
                        <Pressable
                          onPress={() => onExpandPreview(message.reportPreview!)}
                          style={styles.previewActionButton}
                          testID={`AssistantChatMessageBubble.Preview.${message.id}`}
                          accessibilityRole="button"
                          accessibilityLabel={t('Expand preview')}
                        >
                        <Icon name="search" size={18} color={Color.premiumBlack} />
                      </Pressable>
                    </View>
                  </Tooltip>
                )}
                {showDownloadAction && (
                  <Tooltip
                    label={canDownloadReport ? t('Download full report') : t('Report is still generating')}
                    visible={
                      hoveredPreviewAction?.messageId === message.id &&
                      hoveredPreviewAction?.action === 'download'
                    }
                    style={styles.previewActionWrapper}
                  >
                    <View
                      onPointerEnter={() => setHoveredPreviewAction({ messageId: message.id, action: 'download' })}
                      onPointerLeave={() => setHoveredPreviewAction((current) => current?.messageId === message.id && current?.action === 'download' ? null : current,)
                      }
                    >
                      <Pressable
                        disabled={!canDownloadReport}
                        onPress={canDownloadReport ? () => onDownloadReport(message.reportUrl) : undefined}
                        style={[styles.previewActionButton, !canDownloadReport && styles.previewActionButtonDisabled]}
                        testID={`AssistantChatMessageBubble.Download.${message.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={t('Download report')}
                        accessibilityState={{ disabled: !canDownloadReport }}
                      >
                        {canDownloadReport ? (
                          downloadIcon
                        ) : (
                          <div style={styles.disabledDownloadIcon as any}>{downloadIcon}</div>
                        )}
                      </Pressable>
                    </View>
                  </Tooltip>
                )}
              </View>
            )}
          </View>
        )}
        {hasStatusMessage && (
          <View style={styles.statusMessageContainer}>
            <View style={styles.statusMessageHeader}>
              <Text style={styles.statusMessageText}>{message.statusMessage}</Text>
              <AnimatedDots />
            </View>
          </View>
        )}
        <View style={[styles.messageMetaRow, isUser ? styles.userMetaRow : styles.assistantMetaRow]}>
          <Text style={timestampStyles}>{formatTimestamp(message.createdAt)}</Text>
          <View style={styles.messageActions}>
            <Tooltip
              label={copiedMessageId === message.id ? t('Copied!') : t('Copy message')}
              visible={isCopyTooltipVisible || copiedMessageId === message.id}
              style={styles.messageActionWrapper}
              showArrow={true}
              placement='bottom'
            >
              <View
                onPointerEnter={() => setHoveredMessageAction({ messageId: message.id, action: 'copy' })}
                onPointerLeave={() =>
                  setHoveredMessageAction((current) => (current?.messageId === message.id ? null : current))
                }
              >
                <Pressable
                  onPress={() => onCopyMessage(message.id, copyContent)}
                  style={styles.messageActionButton}
                  accessibilityRole="button"
                  accessibilityLabel={t('Copy message')}
                >
                  <Icon name="copy" size={16} color={Color.premiumBlack} />
                </Pressable>
              </View>
            </Tooltip>
          </View>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  messageRow: {
    marginBottom: Spacing.M,
  },
  messageWrapper: {
    maxWidth: '80%',
    alignItems: 'flex-end',
  },
  userMessage: {
    alignItems: 'flex-end',
  },
  userWrapper: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
  },
  assistantMessage: {
    alignItems: 'flex-start',
  },
  assistantWrapper: {
    alignSelf: 'flex-start',
    alignItems: 'flex-start',
  },
  messageBubble: {
    padding: Spacing.S,
    borderRadius: Radius.M,
  },
  userBubble: {
    backgroundColor: Color.sleepingGrey,

  },
  assistantBubble: {
    backgroundColor: Color.sleepingGrey,
    maxWidth: '100%'
  },
  messageActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: Spacing.XS,
  },
  messageActionWrapper: {
    position: 'relative',
  },
  messageActionButton: {
    padding: Spacing.XS / 2,
    borderRadius: Radius.S,
  },
  userText: {
    color: Color.premiumBlack,
  },
  assistantText: {
    color: Color.premiumBlack,
  },
  errorBubble: {
    backgroundColor: 'transparent',
    padding: 0,
  },
  errorText: {
    color: Color.darkRed,
  },
  statusText: {
    color: Color.graphicGrey,
    fontStyle: 'italic',
    marginTop: Spacing.XS,
  },
  previewContainer: {
    marginTop: Spacing.S,
    borderRadius: Radius.S,
    gap: Spacing.S,
  },
  previewError: {
    color: Color.darkRed,
  },
  previewFooter: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  previewActionButton: {
    padding: Spacing.XS,
    borderRadius: Radius.S,
    borderWidth: 1,
    borderColor: Color.calmGrey,
  },
  previewActionButtonDisabled: {
    opacity: 0.9,
    backgroundColor: Color.simplyWhite,
  },
  previewActionWrapper: {
    position: 'relative',
    padding: Spacing.XS / 16,
  },
  disabledDownloadIcon: {
    display: 'flex',
    filter: 'blur(0.7px)',
    opacity: 0.8,
  },
  statusMessageContainer: {
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    alignSelf: 'flex-start',
    marginTop: Spacing.XS,
  },
  statusMessageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusMessageText: {
    color: Color.graphiteGrey,
    marginRight: Spacing.XS,
  },
  timestampText: {
    marginTop: Spacing.XS / 2,
    fontSize: 10,
    color: Color.graphiteGrey,
    textTransform: 'lowercase',
  },
  messageMetaRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.S,
  },
  userMetaRow: {
    justifyContent: 'flex-end',
  },
  assistantMetaRow: {
    justifyContent: 'flex-start',
  },
  userTimestamp: {
    alignSelf: 'flex-start',
  },
  assistantTimestamp: {
    alignSelf: 'flex-start',
  },
})

export default ChatMessageBubble
